import RandomIdGenerator from '@/utils/RandomIdGenerator';
import { DEFAULT_CALENDAR_ID } from '@/utils/ExcelProcessorUtils';
import { buildNewTaskLock, echoExistingLock, isTimeRangeEditable } from '@/utils/TaskLockUtils';
import { PROBLEM_KINDS } from '@/utils/ProposalProblems';

const DURATION_UNITS = new Set(['minute', 'hour', 'day', 'week', 'month', 'year']);
const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_ERRORS = 30;
// How many structured problems we keep the detail of. Counts are always exact
// (they drive the "542 of 585" headline the agent branches on); the objects
// themselves are only ever used for samples, so retaining one per task on a
// 57k-task tree would be pure waste.
const MAX_PROBLEMS = 200;

/**
 * Hydrate an RPilot schedule proposal into the full task shape the commit API
 * expects, and validate everything that can't be hydrated.
 *
 * The agent is only responsible for the semantic content of a proposal (tree
 * structure, titles, dates, subtask intent, dependencies). Everything
 * mechanical is filled in here:
 *  - task ids, parentTaskId, childTaskIds, wbs/code/sort, scheduleId. A task
 *    keeps its proposed id only when that id belongs to a task in the loaded
 *    tree (the agent is mutating an existing task). Every other id — missing
 *    or agent-invented — is minted fresh: ids like "TASK0001" that a model
 *    likes to hand out have usually been committed before under another
 *    schedule, and the API 422s the whole commit ("Invalid TaskIds found in
 *    temporal history with different schedules"). Prerequisite references to
 *    an agent-invented id are rewritten to the minted one, and the
 *    proposed→assigned pairs come back as `taskIdMap` so the caller can tell
 *    the agent which ids its tasks actually received.
 *  - subtask ids and bookkeeping fields (meta.versionReference, itemId,
 *    createdBy*, progress), preserving identity of existing subtasks
 *  - missing task fields on updates inherited from the live task, so sparse
 *    proposals don't read as deletions or produce false diffs
 *  - timeRanges rows per schedule-template declaration, with calendar
 *    resolution (schedule mapping → existing row → seed calendar)
 *
 * Anything wrong or missing that can't be safely hydrated (no title, unknown
 * template codes/team/location ids, unresolvable or cyclic prerequisites,
 * missing dates on tracking ranges, edits to locked tasks) is reported twice
 * over: as `errors` (the flat, trimmed message list callers have always used)
 * and as `problems` — the same failures tagged with a stable `kind` from
 * PROBLEM_KINDS, alongside exact `problemCounts` and the `refCounts`
 * denominators. summarizeProblems() turns the latter into the grouped, hinted
 * rejection the agent receives, which is the only way "542 of 585 location ids
 * are unknown" reads as one fact rather than 30 strings and an ellipsis.
 *
 * @param {Array} proposedTasks nested task tree from the agent
 * @param {Object} context
 * @param {Array}  context.originalTasks live nested tree (empty on create)
 * @param {Object} context.schedule selected schedule (null on create)
 * @param {Object} context.scheduleTemplate schedule template with timeRangeTemplates (null skips time-range hydration)
 * @param {Array}  context.itemTemplates project item templates ({ code, type })
 * @param {Array}  context.teams project teams ({ id })
 * @param {Array}  context.locations project locations ({ id })
 * @param {Function} [context.generateId] id factory override (tests)
 * @returns {{ tasks: Array, errors: string[], problems: Array, problemCounts: Object,
 *             problemTotal: number, refCounts: Object, taskIdMap: Object }} hydrated tree;
 *   `tasks` is only meaningful when `errors` is empty. `problems` is capped at
 *   MAX_PROBLEMS entries (samples); `problemCounts` / `problemTotal` always count
 *   every problem found. `taskIdMap` is { proposedId: assignedId } for every new
 *   task whose agent-supplied id was replaced (empty when none were).
 */
export function hydrateAndValidate(proposedTasks, {
  originalTasks = [],
  schedule = null,
  scheduleTemplate = null,
  itemTemplates = [],
  teams = [],
  locations = [],
  generateId = null,
} = {}) {
  const idGenerator = new RandomIdGenerator();
  const generate = generateId || (() => idGenerator.generate());
  // Problems are structured: every one carries a stable `kind` so the caller can
  // group and count them (see summarizeProblems) instead of shipping the model a
  // wall of near-identical strings. `errors` — the flat, trimmed message list —
  // is kept for existing callers and reads exactly as it did before.
  const problems = [];
  const problemCounts = {};
  const refCounts = { location: 0, team: 0, itemTemplateCode: 0 };
  let problemTotal = 0;
  // Minted id → the placeholder the agent proposed for it (filled in pass 1).
  // Problems go back to the agent, which only knows the ids it proposed, so
  // every problem is tagged with that id rather than the minted replacement.
  const proposedIdByAssignedId = new Map();
  const addError = (kind, message, taskId = null, taskTitle = null) => {
    problemTotal += 1;
    problemCounts[kind] = (problemCounts[kind] || 0) + 1;
    if (problems.length < MAX_PROBLEMS) {
      problems.push({
        kind,
        taskId: taskId ? (proposedIdByAssignedId.get(taskId) || taskId) : null,
        taskTitle: taskTitle || null,
        message,
      });
    }
  };

  if (!Array.isArray(proposedTasks) || proposedTasks.length === 0) {
    addError(PROBLEM_KINDS.EMPTY_PROPOSAL, 'The proposal contains no tasks — pass a non-empty task tree.');
    return {
      tasks: [],
      errors: [problems[0].message],
      problems,
      problemCounts,
      problemTotal,
      refCounts,
      taskIdMap: {},
    };
  }

  const originalById = new Map();
  (function indexOriginal(tasks) {
    (tasks || []).forEach((task) => {
      originalById.set(task.id, task);
      indexOriginal(task.children);
    });
  })(originalTasks);

  // Reference data. Empty lookup ⇒ that data wasn't loaded; skip the check
  // rather than rejecting every id.
  const templateKindByCode = new Map();
  (itemTemplates || []).forEach((template) => {
    if (!template?.code) return;
    const kind = template.type === 'IssueTemplate' ? 'issue'
      : template.type === 'ProcessTemplate' ? 'process' : null;
    if (kind) templateKindByCode.set(String(template.code), kind);
  });
  const teamIds = new Set((teams || []).map((team) => String(team.id)));
  const locationIds = new Set((locations || []).map((location) => String(location.id)));
  const declaredRangeTemplates = Array.isArray(scheduleTemplate?.timeRangeTemplates) && scheduleTemplate.timeRangeTemplates.length > 0
    ? scheduleTemplate.timeRangeTemplates
    : null;
  const declaredRangeTemplateIds = (declaredRangeTemplates || []).map((template) => template.id);
  const rangeTemplateTitle = (templateId) =>
    declaredRangeTemplates?.find((template) => template.id === templateId)?.title || templateId;
  const scheduleId = schedule?.id || '';

  const parseDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const dateMs = (value) => parseDate(value)?.getTime() ?? null;
  const provided = (source, key) =>
    Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined;

  // ── Pass 1: clone the tree and settle task ids, so pass 2 can resolve
  // parent/child/prerequisite references against the final id set.
  //
  // Only an id that names a task in the loaded tree survives — that is the
  // agent saying "mutate this task". Anything else is a placeholder the agent
  // made up (or omitted) for a NEW task, and it is replaced with a minted id:
  // the API keeps a temporal history of every task id it has ever stored, and
  // rejects a commit that reuses one under a different schedule. Placeholders
  // still matter as reference handles, so proposed→assigned is remembered and
  // prerequisiteTaskIds are rewritten through it in pass 2.
  const usedTaskIds = new Set();
  const seenProposedIds = new Set();
  const assignedIdByProposedId = new Map();
  const mintId = () => {
    let id;
    do { id = generate(); } while (usedTaskIds.has(id) || originalById.has(id));
    return id;
  };
  const cloneWithIds = (task, path) => {
    if (!task || typeof task !== 'object' || Array.isArray(task)) {
      addError(PROBLEM_KINDS.NOT_A_TASK, `${path}: not a task object.`);
      return null;
    }
    const clone = { ...task };
    const proposedId = (typeof clone.id === 'string' && clone.id.trim()) ? clone.id.trim() : '';
    if (!proposedId) {
      clone.id = mintId();
    } else {
      if (seenProposedIds.has(proposedId)) {
        addError(PROBLEM_KINDS.DUPLICATE_TASK_ID,
          `${path}: duplicate task id "${proposedId}" — every task needs a unique id (references would be ambiguous).`,
          proposedId, clone.title);
      }
      seenProposedIds.add(proposedId);
      if (originalById.has(proposedId)) {
        clone.id = proposedId;
      } else {
        clone.id = mintId();
        // First occurrence wins the alias; a duplicate placeholder has already
        // been reported above and cannot be resolved unambiguously anyway.
        if (!assignedIdByProposedId.has(proposedId)) assignedIdByProposedId.set(proposedId, clone.id);
        proposedIdByAssignedId.set(clone.id, proposedId);
      }
    }
    usedTaskIds.add(clone.id);
    clone.children = Array.isArray(task.children)
      ? task.children.map((child, i) => cloneWithIds(child, `${path} > child ${i + 1}`)).filter(Boolean)
      : [];
    return clone;
  };
  const tree = proposedTasks
    .map((task, i) => cloneWithIds(task, `Task ${i + 1}`))
    .filter(Boolean);
  // Labels quote the id the agent proposed, never the minted replacement.
  const agentFacingId = (id) => proposedIdByAssignedId.get(id) || id;
  const resolveTaskRef = (id) => assignedIdByProposedId.get(id) || id;
  const taskIdMap = Object.fromEntries(assignedIdByProposedId);

  // ── Subtasks ─────────────────────────────────────────────────────────────
  const usedSubtaskIds = new Set();

  const hydrateSubtasks = (proposedSubtasks, originalTask, taskId, taskLabel, taskTitle) => {
    const originalSubtasks = originalTask?.subtasks || [];
    const claimedOriginalIds = new Set();

    return proposedSubtasks.map((subtask, i) => {
      if (!subtask || typeof subtask !== 'object' || Array.isArray(subtask)) {
        addError(PROBLEM_KINDS.INVALID_SUBTASK, `${taskLabel}: subtask ${i + 1} is not an object.`, taskId, taskTitle);
        return null;
      }
      const label = `${taskLabel}, subtask ${i + 1}${typeof subtask.title === 'string' && subtask.title ? ` "${subtask.title}"` : ''}`;

      // Re-attach to the live subtask when possible: by id, else by
      // title+template (protects identity when the agent's transform drops ids).
      let match = null;
      if (typeof subtask.id === 'string' && subtask.id.trim()) {
        match = originalSubtasks.find((original) => original.id === subtask.id.trim()) || null;
      } else {
        match = originalSubtasks.find((original) =>
          !claimedOriginalIds.has(original.id) &&
          original.title === subtask.title &&
          (original.itemTemplateCode || '') === (subtask.itemTemplateCode || '')) || null;
      }

      let id = (typeof subtask.id === 'string' && subtask.id.trim()) || match?.id || generate();
      if (usedSubtaskIds.has(id)) {
        // Same id proposed twice — the first occurrence keeps the identity,
        // this one becomes a brand-new subtask.
        match = null;
        do { id = generate(); } while (usedSubtaskIds.has(id));
      }
      usedSubtaskIds.add(id);
      if (match) claimedOriginalIds.add(match.id);

      let title = subtask.title;
      if (typeof title !== 'string' || !title.trim()) {
        if (match) {
          title = match.title;
        } else {
          addError(PROBLEM_KINDS.MISSING_SUBTASK_TITLE, `${label}: subtask title is required.`, taskId, taskTitle);
          title = '';
        }
      }

      const itemTemplateCode = typeof subtask.itemTemplateCode === 'string'
        ? subtask.itemTemplateCode.trim()
        : (match?.itemTemplateCode || '');
      const templateKind = itemTemplateCode && templateKindByCode.size > 0
        ? templateKindByCode.get(itemTemplateCode)
        : undefined;
      if (itemTemplateCode && templateKindByCode.size > 0 && templateKind === undefined) {
        addError(PROBLEM_KINDS.UNKNOWN_ITEM_TEMPLATE_CODE,
          `${label}: itemTemplateCode "${itemTemplateCode}" does not match any process/issue template in this project — use rdrive_search_item_templates to find valid codes.`,
          taskId, taskTitle);
      }

      let itemType = subtask.itemType ?? match?.itemType;
      if (itemType !== 'process' && itemType !== 'issue') {
        if (templateKind) {
          itemType = templateKind;
        } else {
          addError(PROBLEM_KINDS.INVALID_ITEM_TYPE,
            `${label}: itemType must be "process" or "issue"${itemType !== undefined ? ` (got ${JSON.stringify(itemType)})` : ''}.`,
            taskId, taskTitle);
        }
      } else if (templateKind && templateKind !== itemType) {
        addError(PROBLEM_KINDS.ITEM_TYPE_TEMPLATE_CONFLICT,
          `${label}: itemType "${itemType}" conflicts with template "${itemTemplateCode}", which is a ${templateKind} template.`,
          taskId, taskTitle);
      }

      let weighting = subtask.weighting;
      if (weighting === undefined || weighting === null) {
        weighting = match?.weighting ?? 1;
      } else if (!Number.isFinite(weighting) || weighting < 0) {
        addError(PROBLEM_KINDS.INVALID_WEIGHTING,
          `${label}: weighting must be a number ≥ 0 (got ${JSON.stringify(weighting)}).`, taskId, taskTitle);
        weighting = 1;
      }

      const leadByTeamId = subtask.leadByTeamId ?? match?.leadByTeamId ?? '';
      if (leadByTeamId && teamIds.size > 0) {
        refCounts.team += 1;
        if (!teamIds.has(String(leadByTeamId))) {
          addError(PROBLEM_KINDS.UNKNOWN_TEAM_ID,
            `${label}: leadByTeamId "${leadByTeamId}" does not match any team in this project — use rdrive_search_teams to find valid ids.`,
            taskId, taskTitle);
        }
      }

      let meta = (subtask.meta && typeof subtask.meta === 'object') ? subtask.meta : (match?.meta || {});
      // Only a brand-new subtask gets a generated versionReference: an existing
      // one keeps whatever the API returned (possibly none), or the diff would
      // report a meta change on every subtask stored without one.
      if (!match && !meta.versionReference) meta = { ...meta, versionReference: generate() };

      // No createdBy* / parentTaskId: the API's Subtask model has no such
      // fields (SubTaskDTO carries id, title, leadByTeamId, weighting, itemId,
      // itemType, itemTemplateCode, meta, progress, scheduleId, tags) and
      // MapFromDTO drops anything else. Inventing them here made the validator
      // flag every task with a subtask as "updated" on a no-op transform.
      return {
        ...(match || {}),
        ...subtask,
        id,
        title,
        itemType,
        itemTemplateCode,
        weighting,
        leadByTeamId,
        itemId: subtask.itemId ?? match?.itemId ?? '',
        progress: Number.isFinite(subtask.progress) ? subtask.progress : (match?.progress ?? 0),
        meta,
        scheduleId,
      };
    }).filter(Boolean);
  };

  // ── Time ranges ──────────────────────────────────────────────────────────
  const hydrateTimeRanges = (task, originalTask, taskLabel, taskTitle) => {
    let proposedRows;
    if (provided(task, 'timeRanges')) {
      if (Array.isArray(task.timeRanges)) {
        proposedRows = task.timeRanges;
      } else {
        addError(PROBLEM_KINDS.INVALID_TIME_RANGES, `${taskLabel}: timeRanges must be an array.`, task.id, taskTitle);
        proposedRows = [];
      }
    } else {
      proposedRows = originalTask?.timeRanges || [];
    }

    // Without template detail we can't know the declared set or types — pass
    // the rows through untouched rather than guess.
    if (!declaredRangeTemplates) return proposedRows;

    const declaredIds = declaredRangeTemplates.map((template) => template.id);
    const rowsByTemplate = new Map();
    proposedRows.forEach((row) => {
      if (!row || typeof row !== 'object') {
        addError(PROBLEM_KINDS.INVALID_TIME_RANGE_ROW, `${taskLabel}: time range row is not an object.`, task.id, taskTitle);
        return;
      }
      if (!declaredIds.includes(row.timeRangeTemplateId)) {
        addError(PROBLEM_KINDS.UNKNOWN_TIME_RANGE_TEMPLATE_ID,
          `${taskLabel}: time range references unknown timeRangeTemplateId ${JSON.stringify(row.timeRangeTemplateId)} — this schedule template declares: ${declaredIds.join(', ')}.`,
          task.id, taskTitle);
        return;
      }
      if (rowsByTemplate.has(row.timeRangeTemplateId)) {
        addError(PROBLEM_KINDS.DUPLICATE_TIME_RANGE_ROW,
          `${taskLabel}: duplicate time range rows for template "${row.timeRangeTemplateId}" — exactly one row per template.`,
          task.id, taskTitle);
        return;
      }
      rowsByTemplate.set(row.timeRangeTemplateId, row);
    });

    const originalRowsByTemplate = new Map(
      (originalTask?.timeRanges || []).map((row) => [row.timeRangeTemplateId, row])
    );
    // When a tracking row is missing entirely, borrow the window from another
    // proposed row (the prompt tells agents all rows may share one window).
    const donorRow = [...rowsByTemplate.values()].find((row) => parseDate(row.startDate)) || null;

    return declaredRangeTemplates.map((template) => {
      const templateLabel = template.title || template.id;
      const originalRow = originalRowsByTemplate.get(template.id) || null;
      let row = rowsByTemplate.get(template.id) || originalRow;
      if (!row) {
        if (template.type === 'none') {
          row = { startDate: null, endDate: null, duration: { value: 1, unit: 'day' }, progress: 0 };
        } else if (donorRow) {
          row = { startDate: donorRow.startDate, endDate: donorRow.endDate ?? null, duration: donorRow.duration, progress: 0 };
        } else {
          addError(PROBLEM_KINDS.MISSING_TIME_RANGE,
            `${taskLabel}: no time range for template "${templateLabel}" and no dates to hydrate it from — include a row with a startDate for it.`,
            task.id, taskTitle);
          return null;
        }
      }

      const start = parseDate(row.startDate);
      if (row.startDate && !start) {
        addError(PROBLEM_KINDS.INVALID_DATE,
          `${taskLabel}: time range "${templateLabel}" has an unparseable startDate ${JSON.stringify(row.startDate)} — use ISO 8601.`,
          task.id, taskTitle);
      }
      const end = parseDate(row.endDate);
      if (row.endDate && !end) {
        addError(PROBLEM_KINDS.INVALID_DATE,
          `${taskLabel}: time range "${templateLabel}" has an unparseable endDate ${JSON.stringify(row.endDate)} — use ISO 8601.`,
          task.id, taskTitle);
      }
      if (template.type !== 'none' && !start) {
        addError(PROBLEM_KINDS.MISSING_START_DATE,
          `${taskLabel}: time range "${templateLabel}" requires a startDate (template type "${template.type}").`,
          task.id, taskTitle);
      }
      if (start && end && end.getTime() < start.getTime()) {
        addError(PROBLEM_KINDS.END_BEFORE_START,
          `${taskLabel}: time range "${templateLabel}" ends (${row.endDate}) before it starts (${row.startDate}).`,
          task.id, taskTitle);
      }

      let duration = row.duration;
      if (duration === undefined || duration === null) {
        const days = start && end ? Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY_MS)) : 1;
        duration = { value: days, unit: 'day' };
      } else if (typeof duration !== 'object' || !Number.isFinite(duration.value) || duration.value < 0 || !DURATION_UNITS.has(duration.unit)) {
        addError(PROBLEM_KINDS.INVALID_DURATION,
          `${taskLabel}: time range "${templateLabel}" duration must be { value: number ≥ 0, unit: minute|hour|day|week|month|year } (got ${JSON.stringify(duration)}).`,
          task.id, taskTitle);
        duration = { value: 1, unit: 'day' };
      }
      // The server renormalizes weighting from value+unit against the
      // calendar; a placeholder just has to be present.
      if (duration.weighting === undefined || duration.weighting === null) {
        duration = { ...duration, weighting: duration.value };
      }

      // Calendar: agent's explicit choice → schedule mapping → live row →
      // seed calendar. Tracking ranges must resolve to something or the API
      // rejects the commit; 'none' ranges take no calendar.
      const mapping = (schedule?.timeRangeCalendars || []).find(
        (candidate) => candidate.timeRangeTemplateId === template.id
      ) || {};
      const calendarId = template.type === 'none'
        ? null
        : (row.calendarId || mapping.calendarId || originalRow?.calendarId || DEFAULT_CALENDAR_ID);
      const calendarVersionReference = template.type === 'none'
        ? null
        : (row.calendarId
          ? (row.calendarVersionReference ?? null)
          : (mapping.calendarId
            ? (mapping.calendarVersionReference ?? null)
            : (originalRow?.calendarVersionReference ?? null)));

      let progress = row.progress;
      if (!Number.isFinite(progress) || progress < 0 || progress > 100) {
        if (progress !== undefined && progress !== null) {
          addError(PROBLEM_KINDS.INVALID_PROGRESS,
            `${taskLabel}: time range "${templateLabel}" progress must be a number between 0 and 100 (got ${JSON.stringify(progress)}).`,
            task.id, taskTitle);
        }
        progress = Number.isFinite(originalRow?.progress) ? originalRow.progress : 0;
      }

      return {
        ...(originalRow || {}),
        ...row,
        timeRangeTemplateId: template.id,
        timeRangeTemplateType: template.type,
        startDate: start ? start.toISOString() : null,
        endDate: end ? end.toISOString() : null,
        duration,
        calendarId,
        calendarVersionReference,
        progress,
      };
    }).filter(Boolean);
  };

  // ── Lock enforcement (mirrors ExcelProcessorValidation semantics) ────────
  // leadByTeamId is deliberately absent: it's an RDrive-only field that stays
  // editable per subtask even when the profile locks the subtask's identity.
  const subtaskLockTuple = (subtasks) => (subtasks || []).map((subtask) => [
    subtask.title, subtask.itemType, subtask.itemTemplateCode || '', subtask.weighting,
  ]);
  const sameTuples = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  // Start, end and duration are protected per time range template via
  // lock.editableTimeRangeTemplateIds; progress stays editable. A lock
  // without the ids array is legacy and resolves as fully editable.
  const changedLockedRangeIds = (hydrated, originalTask) => {
    const lockedIds = [...new Set(
      [...(originalTask.timeRanges || []), ...(hydrated.timeRanges || [])]
        .map((row) => row.timeRangeTemplateId).filter(Boolean),
    )].filter((templateId) => !isTimeRangeEditable(originalTask.lock, templateId));
    return lockedIds.filter((templateId) => {
      const rowOf = (rows) => (rows || []).find((row) => row.timeRangeTemplateId === templateId);
      const newRow = rowOf(hydrated.timeRanges);
      const originalRow = rowOf(originalTask.timeRanges);
      if (!newRow && !originalRow) return false;
      if (!newRow || !originalRow) return true;
      return !(
        dateMs(newRow.startDate) === dateMs(originalRow.startDate) &&
        dateMs(newRow.endDate) === dateMs(originalRow.endDate) &&
        (newRow.duration?.value ?? null) === (originalRow.duration?.value ?? null) &&
        (newRow.duration?.unit ?? null) === (originalRow.duration?.unit ?? null)
      );
    });
  };

  const checkLocks = (hydrated, originalTask, parentChanged, taskLabel) => {
    const lock = originalTask.lock || {};
    const changedLocked = changedLockedRangeIds(hydrated, originalTask);
    if (changedLocked.length > 0) {
      addError(PROBLEM_KINDS.LOCKED_TIME_RANGE,
        `${taskLabel}: start, end and duration cannot be changed for locked time range(s): ${changedLocked.map(rangeTemplateTitle).join(', ')}. Re-propose them with their original dates.`,
        originalTask.id, originalTask.title);
    }
    if (lock.structureEditable === false && (hydrated.title !== originalTask.title || parentChanged)) {
      addError(PROBLEM_KINDS.LOCKED_STRUCTURE,
        `${taskLabel}: title/position cannot be changed — the task is structurally locked. Re-propose it unchanged.`,
        originalTask.id, originalTask.title);
    }
    if (lock.subtasksEditable === false &&
        !sameTuples(subtaskLockTuple(hydrated.subtasks), subtaskLockTuple(originalTask.subtasks))) {
      addError(PROBLEM_KINDS.LOCKED_SUBTASKS,
        `${taskLabel}: subtasks cannot be changed — the task's subtasks are locked. Re-propose them unchanged.`,
        originalTask.id, originalTask.title);
    }
    if (originalTask.key) {
      const originalPrereqs = [...new Set(originalTask.prerequisiteTaskIds || [])].sort();
      const newPrereqs = [...new Set(hydrated.prerequisiteTaskIds || [])].sort();
      if (JSON.stringify(originalPrereqs) !== JSON.stringify(newPrereqs)) {
        addError(PROBLEM_KINDS.LOCKED_PREREQUISITES,
          `${taskLabel}: prerequisiteTaskIds cannot be changed — the task is locked with key "${originalTask.key}". Keep its original list.`,
          originalTask.id, originalTask.title);
      }
    }
  };

  // New tasks default to fully editable time ranges (every declared template).
  // An explicitly proposed lock is kept: its editable ids are validated
  // against the schedule template, a missing ids array is filled with the
  // open default, and the legacy timeRangesEditable:false flag translates to
  // "no editable time ranges".
  const resolveNewTaskLock = (task, hydratedTimeRanges, taskLabel) => {
    const openIds = declaredRangeTemplates
      ? declaredRangeTemplateIds
      : [...new Set((hydratedTimeRanges || []).map((row) => row.timeRangeTemplateId).filter(Boolean))];
    const proposed = (task.lock && typeof task.lock === 'object' && !Array.isArray(task.lock)) ? task.lock : null;
    if (!proposed) return buildNewTaskLock(openIds);
    const { timeRangesEditable, ...rest } = proposed;
    const lock = { subtasksEditable: true, structureEditable: true, ...rest };
    if (!Array.isArray(lock.editableTimeRangeTemplateIds)) {
      lock.editableTimeRangeTemplateIds = timeRangesEditable === false ? [] : [...openIds];
    } else if (declaredRangeTemplates) {
      const unknown = [...new Set(lock.editableTimeRangeTemplateIds)]
        .filter((templateId) => !declaredRangeTemplateIds.includes(templateId));
      if (unknown.length > 0) {
        addError(PROBLEM_KINDS.UNKNOWN_TIME_RANGE_TEMPLATE_ID,
          `${taskLabel}: lock.editableTimeRangeTemplateIds contains unknown time range template id(s): ${unknown.join(', ')} — use ids from the schedule template's timeRangeTemplates.`,
          task.id, task.title);
      }
    }
    return lock;
  };

  // ── Pass 2: hydrate every task ───────────────────────────────────────────
  const hydrateTask = (task, parent, index, parentWbs) => {
    const originalTask = originalById.get(task.id) || null;
    const title = (typeof task.title === 'string' && task.title.trim())
      ? task.title
      : (originalTask?.title ?? '');
    const taskLabel = `Task "${title || agentFacingId(task.id)}" (id ${agentFacingId(task.id)})`;
    if (!title) {
      addError(PROBLEM_KINDS.MISSING_TITLE, `${taskLabel}: "title" is required and cannot be hydrated — provide one.`, task.id, title);
    }

    const pick = (key, fallback) => {
      if (provided(task, key)) return task[key];
      if (originalTask && provided(originalTask, key)) return originalTask[key];
      return fallback;
    };
    const pickArray = (key) => {
      const value = pick(key, []);
      if (!Array.isArray(value)) {
        addError(PROBLEM_KINDS.INVALID_ARRAY_FIELD, `${taskLabel}: "${key}" must be an array.`, task.id, title);
        return [];
      }
      return value;
    };

    const wbs = Array.isArray(pick('wbs', null)) ? pick('wbs', null) : [...parentWbs, String(index + 1)];
    const viewableLocationIds = pickArray('viewableLocationIds');
    if (locationIds.size > 0) {
      viewableLocationIds.forEach((id) => {
        refCounts.location += 1;
        if (!locationIds.has(String(id))) {
          addError(PROBLEM_KINDS.UNKNOWN_LOCATION_ID,
            `${taskLabel}: viewableLocationIds contains "${id}", which does not match any location in this project — use rdrive_search_locations to find valid ids.`,
            task.id, title);
        }
      });
    }

    const subtasksInput = pick('subtasks', []);
    let subtasks;
    if (!Array.isArray(subtasksInput)) {
      addError(PROBLEM_KINDS.INVALID_ARRAY_FIELD, `${taskLabel}: "subtasks" must be an array.`, task.id, title);
      subtasks = [];
    } else {
      subtasks = hydrateSubtasks(subtasksInput, originalTask, task.id, taskLabel, title);
    }

    const timeRanges = hydrateTimeRanges(task, originalTask, taskLabel, title);
    const hydrated = {
      ...(originalTask || {}),
      ...task,
      id: task.id,
      title,
      wbs,
      code: typeof pick('code', null) === 'string' ? pick('code', null) : wbs.join('.'),
      sort: Number.isFinite(pick('sort', null)) ? pick('sort', null) : index,
      parentTaskId: parent ? parent.id : '',
      scheduleId,
      subtasks,
      prerequisiteTaskIds: pickArray('prerequisiteTaskIds').map((id) => resolveTaskRef(String(id))),
      viewableLocationIds,
      labels: pickArray('labels'),
      tags: pickArray('tags'),
      meta: (pick('meta', null) && typeof pick('meta', null) === 'object') ? pick('meta', null) : {},
      // key and lock are never the agent's to set on existing tasks; new
      // tasks get the open defaults unless explicitly proposed.
      key: originalTask ? (originalTask.key ?? '') : (typeof task.key === 'string' ? task.key : ''),
      lock: originalTask
        ? echoExistingLock(originalTask)
        : resolveNewTaskLock(task, timeRanges, taskLabel),
      timeRanges,
      children: task.children.map((child, i) => hydrateTask(child, task, i, wbs)),
    };
    hydrated.childTaskIds = hydrated.children.map((child) => child.id);

    if (originalTask) {
      const parentChanged = (hydrated.parentTaskId || '') !== (originalTask.parentTaskId || '');
      checkLocks(hydrated, originalTask, parentChanged, taskLabel);
    }
    return hydrated;
  };

  const hydratedTree = tree.map((task, i) => hydrateTask(task, null, i, []));

  // ── Sibling codes must be unique ─────────────────────────────────────────
  // Mirrors the API's commit validation (FindInvalidTaskCodeIds /
  // FindInvalidWbsIds): duplicate codes among siblings — compared
  // case-insensitively — 422 the whole commit with "Task X has invalid
  // Code." / "invalid WBS." (the WBS path is built from the codes). Codes
  // the hydrator derives from tree position are unique by construction;
  // only agent-supplied codes can collide.
  (function checkSiblingCodes(tasks, parentLabel) {
    const byCode = new Map();
    tasks.forEach((task) => {
      const key = String(task.code ?? '').toLowerCase();
      const first = byCode.get(key);
      if (first) {
        addError(PROBLEM_KINDS.DUPLICATE_SIBLING_CODE,
          `Tasks "${first.title || first.id}" (id ${first.id}) and "${task.title || task.id}" (id ${task.id}) `
          + `share the sibling code "${task.code}" under ${parentLabel} — sibling codes must be unique `
          + '(case-insensitive); the API rejects the commit otherwise. Renumber or omit "code" to derive it from position.',
          task.id, task.title);
      } else {
        byCode.set(key, task);
      }
    });
    tasks.forEach((task) => checkSiblingCodes(task.children || [], `task "${task.title || task.id}"`));
  })(hydratedTree, 'the schedule root');

  // ── Prerequisites: every id must resolve, no cycles ──────────────────────
  const prereqsById = new Map();
  const labelsById = new Map();
  const titlesById = new Map();
  (function collect(tasks) {
    tasks.forEach((task) => {
      prereqsById.set(task.id, task.prerequisiteTaskIds || []);
      labelsById.set(task.id, `Task "${task.title || agentFacingId(task.id)}" (id ${agentFacingId(task.id)})`);
      titlesById.set(task.id, task.title || null);
      collect(task.children);
    });
  })(hydratedTree);

  prereqsById.forEach((prereqs, id) => {
    prereqs.forEach((prereqId) => {
      if (prereqId === id) {
        addError(PROBLEM_KINDS.SELF_PREREQUISITE, `${labelsById.get(id)}: lists itself as a prerequisite.`,
          id, titlesById.get(id));
      } else if (!prereqsById.has(prereqId)) {
        addError(PROBLEM_KINDS.UNKNOWN_PREREQUISITE,
          originalById.has(prereqId)
            ? `${labelsById.get(id)}: prerequisite "${prereqId}" refers to a task this proposal deletes — drop the link or keep the task.`
            : `${labelsById.get(id)}: prerequisite "${prereqId}" does not resolve to any task in the proposal or the schedule.`,
          id, titlesById.get(id));
      }
    });
  });

  // Iterative DFS cycle detection over the proposal's dependency graph.
  const state = new Map(); // id → 1 visiting, 2 done
  prereqsById.forEach((_, rootId) => {
    if (state.get(rootId)) return;
    const stack = [[rootId, 0]];
    while (stack.length > 0) {
      const frame = stack[stack.length - 1];
      const [id] = frame;
      if (frame[1] === 0) state.set(id, 1);
      const edges = (prereqsById.get(id) || []).filter((prereqId) => prereqsById.has(prereqId));
      if (frame[1] < edges.length) {
        const next = edges[frame[1]++];
        if (state.get(next) === 1) {
          addError(PROBLEM_KINDS.CIRCULAR_PREREQUISITE,
            `${labelsById.get(next)}: circular prerequisite chain detected — remove the loop.`,
            next, titlesById.get(next));
          state.set(next, 2);
        } else if (!state.get(next)) {
          stack.push([next, 0]);
        }
      } else {
        state.set(id, 2);
        stack.pop();
      }
    }
  });

  // Deleting a structurally locked task is a lock violation too.
  originalById.forEach((originalTask, id) => {
    if (originalTask.lock?.structureEditable === false && !usedTaskIds.has(id)) {
      addError(PROBLEM_KINDS.LOCKED_TASK_DELETED,
        `Task "${originalTask.title}" (id ${id}): cannot be deleted — the task is structurally locked. Keep it in the proposal.`,
        id, originalTask.title);
    }
  });

  const messages = problems.map((problem) => problem.message);
  const trimmed = problemTotal > MAX_ERRORS
    ? [...messages.slice(0, MAX_ERRORS), `…and ${problemTotal - MAX_ERRORS} more problems. Fix the above and re-propose; remaining issues will be reported next round.`]
    : messages;

  return { tasks: hydratedTree, errors: trimmed, problems, problemCounts, problemTotal, refCounts, taskIdMap };
}
